import {
  parseVersionedPayload,
  sectionsPresent
} from "../savedViews/payloadCodec";
import type {
  AggView,
  PlanByMode,
  SummaryView
} from "../utils/summary/summaryPageHelpers";

/** Payload schema version; bumped only by a shape change that needs migrating. */
export const SUMMARY_VIEW_VERSION = 1;

/** Controls a view asserts when applied; partial so a built-in can speak for one. */
export interface SummaryViewShape {
  planBy?: PlanByMode;
  agg?: AggView;
}

/**
 * Which KPI columns show. The category seeds the set (null = every metric),
 * and ``visibility`` carries the metrics the user showed or hid by hand over
 * it — keyed by metric, so the choice survives an aggregation switch.
 */
export interface SummaryViewColumns {
  category: SummaryView | null;
  visibility?: Record<string, boolean>;
}

/** The two dimensions the tables and rows are cut by. */
export interface SummaryViewDims {
  tableDim: string;
  rowDim: string;
}

/**
 * A summary view's payload, the whole of ``control_state``. Every section is
 * optional: the save dialog's toggles decide which ones a view carries, and
 * a section it omits leaves that part of the page alone.
 */
export interface SummaryViewPayload {
  version: number;
  dataShape?: SummaryViewShape;
  columns?: SummaryViewColumns;
  dims?: SummaryViewDims;
}

/** Which sections a save captures. Mirrors {@link SummaryViewPayload}. */
export interface SummaryViewSections {
  /** Plan-by, aggregation. */
  dataShape: boolean;
  /** Which KPI columns show. */
  columns: boolean;
  /** Table and row dimension. */
  dims: boolean;
}

/** Every section on, the shape the save modal opens with. */
export const ALL_SUMMARY_VIEW_SECTIONS: SummaryViewSections = {
  dataShape: true,
  columns: true,
  dims: true
};

/** The live state a capture reads and a dirty check compares against. */
export interface SummaryViewLive {
  planBy: PlanByMode;
  agg: AggView;
  category: SummaryView | null;
  visibility: Record<string, boolean>;
  tableDim: string;
  rowDim: string;
}

/** Snapshot the toggled sections of the current state. */
export function captureSummaryViewPayload(
  sections: SummaryViewSections,
  live: SummaryViewLive
): SummaryViewPayload {
  const payload: SummaryViewPayload = { version: SUMMARY_VIEW_VERSION };
  if (sections.dataShape) {
    payload.dataShape = {
      planBy: live.planBy,
      agg: live.agg
    };
  }
  if (sections.columns) {
    payload.columns = {
      category: live.category,
      ...(Object.keys(live.visibility).length
        ? { visibility: live.visibility }
        : {})
    };
  }
  if (sections.dims) {
    payload.dims = { tableDim: live.tableDim, rowDim: live.rowDim };
  }
  return payload;
}

export function sectionsOfSummaryView(
  payload: SummaryViewPayload
): SummaryViewSections {
  return sectionsPresent(payload, ALL_SUMMARY_VIEW_SECTIONS);
}

export function parseSummaryViewPayload(
  controlState: string | null | undefined
): SummaryViewPayload | null {
  return parseVersionedPayload(controlState, SUMMARY_VIEW_VERSION);
}

/**
 * Whether the live state has drifted from the applied view. Compares only
 * what the view claims and what the page could take; hand-set column
 * visibility is left out, like the temporal grid's layout, so refining the
 * columns doesn't mark the view.
 */
export function isSummaryViewDirty(
  payload: SummaryViewPayload,
  live: SummaryViewLive
): boolean {
  const shape = payload.dataShape;
  if (shape?.planBy && shape.planBy !== live.planBy) return true;
  if (shape?.agg && shape.agg !== live.agg) return true;
  const columns = payload.columns;
  if (columns && columns.category !== live.category) return true;
  const dims = payload.dims;
  if (
    dims &&
    (dims.tableDim !== live.tableDim || dims.rowDim !== live.rowDim)
  ) {
    return true;
  }
  return false;
}
